import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X, Save, Loader2, Instagram, Facebook, Twitter, Utensils, ShoppingBag, Store, Package, Link2,
} from "lucide-react";
import { api } from "@/lib/api";

const SOCIAL_ITEMS = [
  { id: "instagram", label: "Instagram", icon: Instagram, colour: "#E4405F", hint: "https://instagram.com/yourshop" },
  { id: "facebook",  label: "Facebook",  icon: Facebook,  colour: "#1877F2", hint: "https://facebook.com/yourshop" },
  { id: "twitter",   label: "X (Twitter)", icon: Twitter, colour: "#1DA1F2", hint: "https://x.com/yourshop" },
];
const MARKET_ITEMS = [
  { id: "swiggy",   label: "Swiggy",   icon: Utensils,    colour: "#FC8019", hint: "https://swiggy.com/restaurants/..." },
  { id: "zomato",   label: "Zomato",   icon: Utensils,    colour: "#E23744", hint: "https://zomato.com/..." },
  { id: "amazon",   label: "Amazon",   icon: ShoppingBag, colour: "#FF9900", hint: "https://amazon.in/shops/..." },
  { id: "flipkart", label: "Flipkart", icon: Store,       colour: "#2874F0", hint: "https://flipkart.com/..." },
  { id: "meesho",   label: "Meesho",   icon: Package,     colour: "#F43397", hint: "https://meesho.com/..." },
];

const seed = (keys, existing) => Object.fromEntries(
  keys.map((k) => [k, { enabled: existing?.[k]?.enabled ?? true, url: existing?.[k]?.url ?? "" }])
);

export default function LinksSheet({ siteId, site, onClose, onChange }) {
  const initialBiz = site.variants?.[site.selected_variant || 0]?.business || {};
  const [socials, setSocials] = useState(() => seed(SOCIAL_ITEMS.map((x) => x.id), initialBiz.socials));
  const [markets, setMarkets] = useState(() => seed(MARKET_ITEMS.map((x) => x.id), initialBiz.marketplaces));
  const [tab, setTab] = useState("social");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const rowSet = (setter) => (id, patch) => setter((s) => ({ ...s, [id]: { ...s[id], ...patch } }));
  const setSocial = rowSet(setSocials);
  const setMarket = rowSet(setMarkets);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/sites/${siteId}/links`, { socials, marketplaces: markets });
      onChange?.(data.site);
      toast.success("Links updated across all 3 designs");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const items = tab === "social" ? SOCIAL_ITEMS : MARKET_ITEMS;
  const state = tab === "social" ? socials : markets;
  const set = tab === "social" ? setSocial : setMarket;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" data-testid="links-sheet" onClick={onClose}>
      <div className="bg-[#161616] border-l border-[#27272A] w-full max-w-md h-full overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-xl font-bold flex items-center gap-2"><Link2 className="w-5 h-5 text-[#C9A24B]" /> Social & Marketplace Links</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-400 mb-5">Only links you fill in will appear on your website. Toggle any off to hide.</p>

        <div className="inline-flex rounded-xl border border-[#27272A] p-1 bg-[#0A0A0A] mb-5">
          <button data-testid="links-tab-social" onClick={() => setTab("social")}
            className={`px-4 py-2 text-xs font-semibold rounded-lg ${tab === "social" ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400"}`}>
            Social Media
          </button>
          <button data-testid="links-tab-market" onClick={() => setTab("market")}
            className={`px-4 py-2 text-xs font-semibold rounded-lg ${tab === "market" ? "bg-[#C9A24B] text-[#0A0A0A]" : "text-zinc-400"}`}>
            Marketplaces
          </button>
        </div>

        <div className="space-y-3 mb-6">
          {items.map((it) => {
            const s = state[it.id];
            const Icon = it.icon;
            return (
              <div key={it.id} data-testid={`links-row-${it.id}`}
                className={`rounded-2xl border p-4 transition-all ${s?.url ? "border-[#C9A24B]/30 bg-[#C9A24B]/5" : "border-[#27272A] bg-[#0A0A0A]"}`}>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${it.colour}22`, color: it.colour }}>
                    <Icon className="w-4.5 h-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{it.label}</div>
                    <div className="text-[11px] text-zinc-500">
                      {s?.url ? (s.enabled ? "Visible on website" : "Hidden") : "Add a link to show on your website"}
                    </div>
                  </div>
                  <Toggle checked={!!s?.enabled} onChange={(v) => set(it.id, { enabled: v })}
                    testid={`links-toggle-${it.id}`} />
                </div>
                <input data-testid={`links-input-${it.id}`}
                  value={s?.url || ""} onChange={(e) => set(it.id, { url: e.target.value })}
                  placeholder={it.hint}
                  className="w-full bg-[#161616] border border-[#27272A] rounded-lg p-2.5 text-xs text-zinc-100 focus:outline-none focus:border-[#C9A24B]" />
              </div>
            );
          })}
        </div>

        <button data-testid="links-save-btn" onClick={save} disabled={saving}
          className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#C9A24B] to-[#E5C378] text-[#0A0A0A] font-bold py-3 rounded-xl disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 wt-spin" /> : <Save className="w-4 h-4" />} Save Links
        </button>
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-all shrink-0 ${checked ? "bg-[#C9A24B]" : "bg-[#27272A]"}`}
      role="switch" aria-checked={checked}>
      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${checked ? "left-[22px]" : "left-0.5"}`} />
    </button>
  );
}
